import { GRID_COLS, GRID_ROWS, TILE_SIZE, CELL, HUB, HUB_EXIT, PLAYER_SPAWN, ENEMY_HUB_POS } from './config';
import { TEMPLATES } from './mapGenerator';
import { tx, ty, HALF } from './gameConstants';
import { isFloorLike } from './gridUtils';
import { classifyAllTiles } from './tileTypes';

/**
 * Build (or rebuild) the entire map visuals for the current stage.
 * Destroys old sprites, renders tiles, walls, decorations, pills.
 */
export function buildMapVisuals(scene, gs, settings) {
  gs.templateIndex = gs.stage - 1;
  const savedLayout = gs.savedLayouts?.[gs.templateIndex];
  if (savedLayout?.grid?.length === GRID_ROWS) {
    gs.grid = savedLayout.grid.map(r => [...r]);
  } else {
    const tpl = TEMPLATES[gs.templateIndex] || TEMPLATES[0];
    gs.grid = tpl.map(r => [...r]);
  }
  const grid = gs.grid;
  const { masks } = classifyAllTiles(grid);

  // Destroy old sprites
  Object.values(gs.pillSprites || {}).forEach(s => s?.destroy());
  Object.values(gs.powerupSprites || {}).forEach(s => s?.destroy());
  gs.pillSprites = {};
  gs.powerupSprites = {};
  if (gs.bonusPallet) { gs.bonusPallet.destroy(); gs.bonusPallet = null; }
  if (gs.bgRT) { gs.bgRT.destroy(); gs.bgRT = null; }

  // Floor tiles (Layer 1)
  for (let r = 0; r < GRID_ROWS; r++)
    for (let c = 0; c < GRID_COLS; c++)
      if (grid[r][c] !== CELL.WALL)
        scene.add.image(c * TILE_SIZE + HALF, r * TILE_SIZE + HALF, 'tile_floor').setDepth(1);

  // Markings (Layer 2)
  for (let r = 0; r < GRID_ROWS; r++) {
    for (let c = 0; c < GRID_COLS; c++) {
      const cell = grid[r][c];
      if (cell === CELL.WALL || cell === CELL.HUB) continue;
      const m = masks[r][c];
      const hasV = m & 3, hasH = m & 12;
      let markKey = null;
      if (hasV && hasH) markKey = 'mark_cross';
      else if (hasH) markKey = 'mark_h';
      else if (hasV) markKey = 'mark_v';
      if (markKey) scene.add.image(c * TILE_SIZE + HALF, r * TILE_SIZE + HALF, markKey).setDepth(2);
    }
  }

  // Walls (Layer 3-5)
  renderWalls(scene, grid);

  // Decorations & Props (Layer 6-8)
  const layoutData = gs.savedLayouts?.[gs.templateIndex];
  if (layoutData?.saved === true) {
    (layoutData.placements || []).forEach(p => {
      const cx = p.col * TILE_SIZE + p.tw * TILE_SIZE / 2;
      const cy = p.row * TILE_SIZE + p.th * TILE_SIZE / 2;
      scene.add.image(cx, cy, p.asset_key).setDepth(p.asset_type === 'prop' ? 8 : 6);
    });
  } else {
    placeDecorationsAndProps(scene, grid);
  }

  // Hub sprite
  scene.add.image(HUB.colStart * TILE_SIZE + 160, HUB.rowStart * TILE_SIZE + 128, 'hub_sprite').setDepth(7);

  // Bake static tiles into RenderTexture
  const excludeFromBake = new Set([gs.player, ...(gs.enemies || [])].filter(Boolean));
  const bgRT = scene.add.renderTexture(0, 0, GRID_COLS * TILE_SIZE, GRID_ROWS * TILE_SIZE);
  bgRT.setOrigin(0, 0).setDepth(0);
  const staticImages = scene.children.list
    .filter(c => c !== bgRT && c.type === 'Image' && !excludeFromBake.has(c))
    .sort((a, b) => (a.depth || 0) - (b.depth || 0));
  staticImages.forEach(s => { bgRT.draw(s); s.destroy(); });
  gs.bgRT = bgRT;

  // Create pills and powerups
  createPillsAndPowerups(scene, gs, grid);
}

function renderWalls(scene, grid) {
  for (let r = 0; r < GRID_ROWS; r++) {
    for (let c = 0; c < GRID_COLS; c++) {
      if (grid[r][c] !== CELL.WALL) continue;
      const cx = c * TILE_SIZE + HALF, cy = r * TILE_SIZE + HALF;
      const fa = isFloorLike(grid, c, r - 1);
      const fb = isFloorLike(grid, c, r + 1);
      const fl = isFloorLike(grid, c - 1, r);
      const fr = isFloorLike(grid, c + 1, r);
      const count = (fa?1:0) + (fb?1:0) + (fl?1:0) + (fr?1:0);
      if (count >= 3) {
        if (fa && fb && fl && fr) scene.add.image(cx, cy, 'tile1x1').setDepth(3);
        else if (fa && fl && fr) scene.add.image(cx, cy, 'tile1_top').setDepth(3);
        else if (fb && fl && fr) scene.add.image(cx, cy, 'tile1_bottom').setDepth(3);
        else if (fa && fb && fl) scene.add.image(cx, cy, 'tile1_left').setDepth(3);
        else scene.add.image(cx, cy, 'tile1_right').setDepth(3);
      } else {
        scene.add.image(cx, cy, 'wall_fill').setDepth(3);
        if (fa && fb) scene.add.image(cx, cy, 'wall_edge_vertical').setDepth(4);
        else { if (fb) scene.add.image(cx, cy, 'wall_edge_bottom').setDepth(4); if (fa) scene.add.image(cx, cy, 'wall_edge_top').setDepth(4); }
        if (fl && fr) scene.add.image(cx, cy, 'wall_edge_horizontal').setDepth(4);
        else { if (fr) scene.add.image(cx, cy, 'wall_edge_right').setDepth(4); if (fl) scene.add.image(cx, cy, 'wall_edge_left').setDepth(4); }
        if (fb && fr) scene.add.image(cx, cy, 'wall_corner_br').setDepth(5);
        if (fb && fl) scene.add.image(cx, cy, 'wall_corner_bl').setDepth(5);
        if (fa && fr) scene.add.image(cx, cy, 'wall_corner_tr').setDepth(5);
        if (fa && fl) scene.add.image(cx, cy, 'wall_corner_tl').setDepth(5);
      }
    }
  }
}

function createPillsAndPowerups(scene, gs, grid) {
  const skip = new Set();
  ENEMY_HUB_POS.forEach(p => skip.add(`${p.col},${p.row}`));
  skip.add(`${HUB_EXIT.col},${HUB_EXIT.row}`);
  skip.add(`${PLAYER_SPAWN.col},${PLAYER_SPAWN.row}`);
  skip.add(`${PLAYER_SPAWN.col - 1},${PLAYER_SPAWN.row}`);
  skip.add(`${PLAYER_SPAWN.col + 1},${PLAYER_SPAWN.row}`);
  for (let r = 0; r < GRID_ROWS; r++) {
    for (let c = 0; c < GRID_COLS; c++) {
      const cell = grid[r][c];
      const cx = tx(c), cy = ty(r);
      if (cell === CELL.POWERUP) {
        gs.powerupSprites[`${c},${r}`] = scene.add.image(cx, cy, 'powerup_sprite').setDepth(5);
        scene.tweens.add({ targets: gs.powerupSprites[`${c},${r}`], alpha: 0.6, duration: 600, yoyo: true, repeat: -1, ease: 'Sine.easeInOut' });
      } else if ((cell === CELL.CORRIDOR || cell === CELL.TUNNEL) && !skip.has(`${c},${r}`)) {
        gs.pillSprites[`${c},${r}`] = scene.add.image(cx, cy, 'coin').setDepth(5);
      }
    }
  }
}

/**
 * Repopulate pills and powerups from grid for a new stage.
 */
export function repopulatePills(scene, gs) {
  const grid = gs.grid;
  const skip = new Set();
  for (let r = HUB.rowStart; r < HUB.rowStart + HUB.rows; r++)
    for (let c = HUB.colStart; c < HUB.colStart + HUB.cols; c++) skip.add(`${c},${r}`);
  skip.add(`${HUB_EXIT.col},${HUB_EXIT.row}`);
  skip.add(`${PLAYER_SPAWN.col},${PLAYER_SPAWN.row}`);
  skip.add(`${PLAYER_SPAWN.col - 1},${PLAYER_SPAWN.row}`);
  skip.add(`${PLAYER_SPAWN.col + 1},${PLAYER_SPAWN.row}`);

  gs.pillSprites = {};
  gs.powerupSprites = {};
  for (let r = 0; r < GRID_ROWS; r++) {
    for (let c = 0; c < GRID_COLS; c++) {
      const cell = grid[r][c];
      const cx = tx(c), cy = ty(r);
      if (cell === CELL.POWERUP) {
        gs.powerupSprites[`${c},${r}`] = scene.add.image(cx, cy, 'powerup_sprite').setDepth(5);
        scene.tweens.add({ targets: gs.powerupSprites[`${c},${r}`], alpha: 0.6, duration: 600, yoyo: true, repeat: -1, ease: 'Sine.easeInOut' });
      } else if ((cell === CELL.CORRIDOR || cell === CELL.TUNNEL) && !skip.has(`${c},${r}`)) {
        gs.pillSprites[`${c},${r}`] = scene.add.image(cx, cy, 'coin').setDepth(5);
      }
    }
  }
}

/**
 * Place decorations and props on wall blocks.
 */
export function placeDecorationsAndProps(scene, grid) {
  const used = Array.from({length: GRID_ROWS}, () => Array(GRID_COLS).fill(false));

  for (let r = HUB.rowStart; r <= HUB.rowStart + 3; r++)
    for (let c = HUB.colStart; c <= HUB.colEnd; c++)
      if (r < GRID_ROWS) used[r][c] = true;
  for (let c = 0; c < GRID_COLS; c++) { used[0][c] = true; used[GRID_ROWS - 1][c] = true; }

  const blocks = [];
  for (let r = 0; r < GRID_ROWS; r++) {
    for (let c = 0; c < GRID_COLS; c++) {
      if (used[r][c] || grid[r][c] !== CELL.WALL) continue;
      let w = 0;
      while (c + w < GRID_COLS && grid[r][c + w] === CELL.WALL && !used[r][c + w]) w++;
      let h = 1;
      while (r + h < GRID_ROWS) {
        let ok = true;
        for (let cc = c; cc < c + w; cc++)
          if (grid[r + h][cc] !== CELL.WALL || used[r + h][cc]) { ok = false; break; }
        if (!ok) break;
        h++;
      }
      for (let rr = r; rr < r + h; rr++)
        for (let cc = c; cc < c + w; cc++) used[rr][cc] = true;
      blocks.push({ col: c, row: r, w, h });
    }
  }

  const DECORS = [
    { key: 'decor_hub_lg', tw: 5, th: 4 },
    { key: 'decor_3x2', tw: 3, th: 2 },
    { key: 'decor_platform', tw: 4, th: 1 },
    { key: 'decor_4x1', tw: 4, th: 1 },
    { key: 'decor_3x1', tw: 3, th: 1 },
    { key: 'decor_2x2', tw: 2, th: 2 },
    { key: 'decor_2x1', tw: 2, th: 1 },
    { key: 'decor_1x1', tw: 1, th: 1 },
  ];
  const PROPS = [
    { key: 'prop_01', tw: 1 }, { key: 'prop_02', tw: 1 },
    { key: 'prop_03', tw: 1 }, { key: 'prop_04', tw: 1 },
    { key: 'prop_2x1', tw: 2 },
  ];

  function h(a, b, c) { return ((a * 2654435761) ^ (b * 2246822519) ^ (c * 3266489917)) >>> 0; }
  const recentDecors = [];
  let propCycle = 0;

  blocks.forEach((b, idx) => {
    const area = b.w * b.h;
    const seed = h(b.col, b.row, b.w + b.h * 7);
    const zone = b.row < 9 ? 0 : b.row > 17 ? 2 : 1;
    const skipDecor = area < 6 && (seed % 100) < 20;

    if (!skipDecor) {
      const fits = DECORS.filter(d => b.w >= d.tw && b.h >= d.th);
      if (fits.length > 0) {
        let pool = fits.filter(d => !recentDecors.includes(d.key));
        if (pool.length === 0) pool = fits;
        const zoneShift = zone * 2 + (b.col > 10 ? 1 : 0);
        const pick = pool[(seed + zoneShift) % pool.length];
        recentDecors.push(pick.key);
        if (recentDecors.length > 3) recentDecors.shift();
        scene.add.image(b.col * TILE_SIZE + b.w * TILE_SIZE / 2, b.row * TILE_SIZE + b.h * TILE_SIZE / 2, pick.key).setDepth(6);
      }
    }

    if (area < 4) return;
    const isEdge = b.col <= 1 || b.col + b.w >= GRID_COLS - 1;
    const isCorner = isEdge && (b.row <= 5 || b.row + b.h >= GRID_ROWS - 5);
    const isLarge = area >= 9;
    const adjFloor = (isFloorLike(grid, b.col - 1, b.row) ? 1 : 0) + (isFloorLike(grid, b.col + b.w, b.row) ? 1 : 0) + (isFloorLike(grid, b.col, b.row - 1) ? 1 : 0) + (isFloorLike(grid, b.col, b.row + b.h) ? 1 : 0);
    let propChance = 28;
    if (isCorner) propChance += 40;
    else if (isEdge) propChance += 22;
    if (isLarge) propChance += 18;
    if (adjFloor >= 2) propChance += 10;
    const pseed = h(b.col + 7, b.row + 11, idx + 3);
    if ((pseed % 100) >= propChance) return;
    let prop = PROPS[propCycle % PROPS.length];
    if (prop.tw > 1 && b.w < 3) { propCycle++; prop = PROPS[propCycle % PROPS.length]; if (prop.tw > 1) { propCycle++; prop = PROPS[propCycle % PROPS.length]; } }
    propCycle++;
    scene.add.image(b.col * TILE_SIZE + b.w * TILE_SIZE / 2, b.row * TILE_SIZE + b.h * TILE_SIZE / 2, prop.key).setDepth(8);
  });
}
